import type { CSSProperties } from 'react';
import type { Book } from '@booknest/shared';

import { pickColors, pickTexture, spineRatios } from './spine-utils.js';

interface BookSpineProps {
  book: Book;
  opening?: boolean;
  onClick?: () => void;
}

/**
 * Tranche de livre — implémentation du composant de référence §12 de
 * `docs/BOOK_SPINE_RECIPE.md`.
 *
 * - Texture appliquée via `pickTexture(book)` qui retourne la chaîne de
 *   classes complète (ex : `'tex-leather-aged gold-band'`).
 * - Couleurs (c1, c2) injectées en CSS vars inline.
 * - Dimensions via `calc(var(--spine-min-*) + (var(--spine-max-*) - var(--spine-min-*)) * R)`.
 */
export function BookSpine({
  book,
  opening = false,
  onClick,
}: BookSpineProps): JSX.Element {
  const [c1, c2] = pickColors(book);
  const texture = pickTexture(book);
  const { heightRatio, widthRatio } = spineRatios(book.id);

  const height = `calc(var(--spine-min-h) + (var(--spine-max-h) - var(--spine-min-h)) * ${heightRatio})`;
  const width = `calc(var(--spine-min-w) + (var(--spine-max-w) - var(--spine-min-w)) * ${widthRatio})`;

  const cssVars: CSSProperties = {
    width,
    height,
    ['--c1' as string]: c1,
    ['--c2' as string]: c2,
  };

  // Sur une tranche assez large, on peut ajouter le nom de famille de l'auteur.
  const showAuthor = widthRatio > 0.5;
  const lastName = book.author.trim().split(/\s+/).pop() ?? '';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${book.title} — ${book.author}`}
      title={`${book.title} — ${book.author}`}
      className={`book-spine ${texture}${opening ? ' opening' : ''}`}
      style={cssVars}
    >
      <span className="band band-top" aria-hidden />
      <span className="spine-text">
        {book.title}
        {showAuthor && lastName ? ` · ${lastName}` : ''}
      </span>
      <span className="band band-bottom" aria-hidden />
    </button>
  );
}
